import { PageId } from "@/types/page";
import {
  ReimbursRequest,
  ApplicationTypeEnum,
  ApproveStatusEnum,
} from "@/types/reimburse";
import { applicationTypeMap, approveStatusMap } from "../utils/approveStatus";
import { ColumnsType } from "antd/es/table";
import { Button, Flex, Space, Tag } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import SText from "@/styles/SText";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

type useReimburseColumnsProps = (props: {
  pageId?: PageId;
  onDetail: (data: ReimbursRequest) => void;
  onOpenTab: (type: "doc", item: ReimbursRequest) => void;
  onSubmit: (reSubmit: boolean, id: string) => Promise<void>;
  onGenerateOrder: (id: string) => void;
}) => ColumnsType<ReimbursRequest>;

const useReimburseColumns: useReimburseColumnsProps = ({
  pageId,
  onDetail,
  onOpenTab,
  onSubmit,
  onGenerateOrder,
}) => {
  const { t } = useTranslation();

  const columns: ColumnsType<ReimbursRequest> = useMemo(
    () => [
      {
        title: t("reimburse.number"),
        dataIndex: "number",
        key: "number",
        width: 200,
        ellipsis: true,
        fixed: "left",
        sorter: (a, b) => {
          const valA = a?.number ?? "";
          const valB = b?.number ?? "";
          return valA.localeCompare(valB);
        },
      },
      {
        title: t("reimburse.applicationTypeLabel"),
        dataIndex: "type",
        key: "type",
        width: 120,
        render: (type: ApplicationTypeEnum) => (
          <Tag color={applicationTypeMap[type].color}>
            {t(applicationTypeMap[type].label)}
          </Tag>
        ),
      },
      {
        title: t("reimburse.title"),
        dataIndex: "title",
        key: "title",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("reimburse.content"),
        dataIndex: "content",
        key: "content",
        width: 200,
        ellipsis: true,
      },

      {
        title: t("reimburse.status"),
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: ApproveStatusEnum) => {
          const { label, color } = approveStatusMap[status];
          return <Tag color={color}>{t(label)}</Tag>;
        },
      },
      {
        title: t("common.createTime"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 180,
        sorter: (a, b) => {
          const valA = a?.createdAt ?? 0;
          const valB = b?.createdAt ?? 0;
          return valA - valB;
        },
        render: (time: number) => dayjs(time).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        title: t("reimburse.submitTime"),
        dataIndex: "submitAt",
        key: "submitAt",
        width: 180,
        sorter: (a, b) => {
          const valA = a?.submitAt ?? 0;
          const valB = b?.submitAt ?? 0;
          return valA - valB;
        },
        render: (time: number) =>
          time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "-",
      },
      {
        title: t("reimburse.completeTime"),
        dataIndex: "completeAt",
        key: "completeAt",
        width: 180,
        sorter: (a, b) => {
          const valA = a?.completeAt ?? 0;
          const valB = b?.completeAt ?? 0;
          return valA - valB;
        },
        render: (time: number) =>
          time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "-",
      },
      {
        title: <SText text={t("common.creator")} />,
        dataIndex: "creator",
        width: 100,
        render: (_, item) => {
          let userData = item.creator;
          return (
            userData && <Flex>
              <div>
                <WMSAvatar creator={userData} uniChar={userData.id}
                  avatarId={userData.avatar}
                  size={24} alt={getUserAvatarTip(userData)}
                />
              </div>
              <SText text={getUserName(userData)} />
            </Flex>
          )
        },
      },
      // {
      //   title: t('common.attachment'),
      //   dataIndex: "relatedDocuments",
      //   fixed: "right",
      //   width: 60,
      //   render: (_, item) => (
      //     <Flex align="center" justify="center" style={{ width: "100%" }}>
      //       <img
      //         src={iconFolder}
      //         alt=""
      //         onClick={() => onOpenTab("doc", item)}
      //         style={{ cursor: "pointer" }}
      //       />
      //     </Flex>
      //   ),
      // },
      {
        title: t("common.opt"),
        fixed: "right",
        key: "opt",
        width: 180,
        render: (_, record) => (
          <Space>
            <Button type="primary" onClick={() => onDetail(record)}>
              {t("common.detail")}
            </Button>
            {pageId === PageId.REIMBURSE_MINE &&
              record.status === ApproveStatusEnum.DRAFT && (
                <Button type="link" onClick={() => onSubmit(false, record.id)}>
                  {t("reimburse.submit")}
                </Button>
              )}
            {pageId === PageId.REIMBURSE_MINE &&
              record.status === ApproveStatusEnum.RETURNED && (
                <Button type="link" onClick={() => onSubmit(false, record.id)}>
                  {t("reimburse.reSubmit")}
                </Button>
              )}
            {pageId === PageId.REIMBURSE_MINE &&
              record.type === ApplicationTypeEnum.MATERIAL_OVER_ISSUE &&
              record.status === ApproveStatusEnum.APPROVED && (
                <Button type="link" onClick={() => onGenerateOrder(record.id)}>
                  {t("requirement.createPreOutInventory")}
                </Button>
              )}
          </Space>
        ),
      },
    ],
    [pageId, t, onDetail, onSubmit, onGenerateOrder],
  );

  return columns;
};

export default useReimburseColumns;
